import React, { useState } from "react";
import { Field } from "redux-form";

import { Typography } from "@braintrust/braintrust-ui-components";
import { recreateOTPAuthBackupCodes } from "@js/apps/auth/actions";
import { MFAMethodSelection } from "@js/apps/common/forms/fields/otp/mfa-method-selection";
import { OTPAuthCodeField } from "@js/apps/common/forms/fields/otp/otp-auth-code";
import {
  MODAL_MODE,
  PasskeyStepUpSubmit,
  useInitialMFAMode,
} from "@js/apps/common/forms/fields/otp/otp-auth-modal";
import { Modal, ModalConfirm } from "@js/components/modal";
import { createFormInstance } from "@js/forms/components";
import { CheckboxField } from "@js/forms/fields/checkbox";

import { openAuthBackupCodesModal } from "../backup-codes-modal";

import styles from "./styles.module.scss";

const RECREATE_OTP_AUTH_BACKUP_CODES_MODAL_ID =
  "recreate-otp-auth-backup-codes-modal";

const ModalInstance = Modal(RECREATE_OTP_AUTH_BACKUP_CODES_MODAL_ID, {
  className: styles.modal,
  closeButton: false,
  keepOnBackdropClick: true,
});

type RecreateOTPAuthBackupCodesFormValue = {
  code?: string;
  is_backup_code?: boolean;
  key?: string;
};

const FormInstance = createFormInstance<
  RecreateOTPAuthBackupCodesFormValue,
  unknown
>("recreate-otp-auth-form");
const initialValues: { is_backup_code: boolean } = { is_backup_code: false };

const closeRecreateOTPAuthBackupCodesModal = () => ModalInstance.close();
export const openRecreateOTPAuthBackupCodesModal = () => ModalInstance.open();

export const RecreateOTPAuthBackupCodesForm = () => {
  const { mode, setMode, bothAvailable } = useInitialMFAMode();
  const [autoStartPasskey, setAutoStartPasskey] = useState(false);
  const onCancel = closeRecreateOTPAuthBackupCodesModal;

  const onSubmit = (values: RecreateOTPAuthBackupCodesFormValue) => {
    return recreateOTPAuthBackupCodes(values)
      .then((data) => openAuthBackupCodesModal(data.backup_codes))
      .then(onCancel);
  };

  return (
    <FormInstance onSubmit={onSubmit} initialValues={initialValues}>
      {mode === MODAL_MODE.SELECT && (
        <MFAMethodSelection
          onSelectOtp={() => setMode(MODAL_MODE.CODE)}
          onSelectPasskey={() => {
            setAutoStartPasskey(true);
            setMode(MODAL_MODE.PASSKEY);
          }}
          onCancel={onCancel}
        />
      )}
      {mode === MODAL_MODE.PASSKEY && (
        <PasskeyStepUpSubmit
          onCancel={bothAvailable ? () => setMode(MODAL_MODE.SELECT) : onCancel}
          cancelLabel={bothAvailable ? "Use different method" : "Cancel"}
          onSubmit={onSubmit}
          autoStart={autoStartPasskey}
        />
      )}
      {mode === MODAL_MODE.CODE && (
        <>
          <Typography
            component="h3"
            variant="title"
            fontWeight={400}
            size="small"
            className="mt0 mb+"
          >
            Recreate 2-Step Authentication Backup Codes
          </Typography>
          <ModalConfirm
            onCancel={
              bothAvailable ? () => setMode(MODAL_MODE.SELECT) : onCancel
            }
            confirmText="Continue"
            cancelText={bothAvailable ? "Use different method" : "Back"}
            onConfirm="submit"
            buttonsSquareShape
            buttonsOnEnds
            confirmButtonVariant="positive-2"
          >
            Enter the 6-digit code generated by your Authenticator app.
            <OTPAuthCodeField />
            <Field
              name="is_backup_code"
              component={CheckboxField}
              label="Use backup code instead"
            />
          </ModalConfirm>
        </>
      )}
    </FormInstance>
  );
};

const RecreateOTPAuthBackupCodesModal = () => {
  return (
    <ModalInstance>
      <RecreateOTPAuthBackupCodesForm />
    </ModalInstance>
  );
};

export default RecreateOTPAuthBackupCodesModal;
